import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DETAILS = [
  { label: "Phone", value: "+1 (437) 453-3942", href: "tel:+14374533942", testid: "contact-phone-link" },
  { label: "Email", value: "northec24@gmail.com", href: "mailto:northec24@gmail.com", testid: "contact-email-link" },
  { label: "Hours", value: "Mon–Sat · 07:00–19:00" },
  { label: "Snow Line", value: "24/7 Winter Dispatch" },
];

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#262626] rounded-none px-0 py-4 font-mono text-sm uppercase tracking-[0.15em] text-[#F2F2F2] placeholder:text-[#4A4A4A] focus:outline-none focus:border-[#FF3B00] focus:ring-0 transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, service: e.detail }));
    window.addEventListener("prefill-service", onPrefill);
    return () => window.removeEventListener("prefill-service", onPrefill);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Pick a service so we route your enquiry to the right crew.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry received. The crew will get back to you within one business day.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Check the fields and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 lg:py-48 border-t border-[#262626]" data-testid="contact-section">
      <div className="max-w-[1800px] mx-auto px-6 sm:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-6">
          <FadeUp>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF3B00] mb-8">Contact — Start a project</p>
          </FadeUp>
          <h2 className="font-oswald font-bold uppercase tracking-tighter leading-[0.85] text-[16vw] sm:text-8xl lg:text-[8vw]" data-testid="contact-heading">
            <MaskedLine>Let's</MaskedLine>
            <MaskedLine delay={0.12}>Talk</MaskedLine>
            <MaskedLine delay={0.24}>
              <span className="text-[#FF3B00]">Build.</span>
            </MaskedLine>
          </h2>
          <FadeUp delay={0.2}>
            <p className="mt-10 text-base sm:text-lg font-light leading-relaxed text-[#808080] max-w-md">
              Tell us what you're building, pouring, remodeling or digging out of.
              We answer every enquiry within one business day — faster when it snows.
            </p>
          </FadeUp>
          <div className="mt-14 space-y-6">
            {DETAILS.map((d, i) => (
              <FadeUp key={d.label} delay={0.25 + i * 0.06}>
                <div className="flex items-baseline gap-6 border-b border-[#1A1A1A] pb-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B00] w-24 shrink-0">
                    {d.label}
                  </span>
                  {d.href ? (
                    <a
                      href={d.href}
                      className="font-mono text-sm sm:text-base uppercase tracking-[0.1em] text-[#F2F2F2] hover:text-[#FF3B00] transition-colors duration-300"
                      data-testid={d.testid}
                    >
                      {d.value}
                    </a>
                  ) : (
                    <span className="font-mono text-sm sm:text-base uppercase tracking-[0.1em] text-[#F2F2F2]">
                      {d.value}
                    </span>
                  )}
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6">
          <FadeUp delay={0.15}>
            <form onSubmit={submit} className="space-y-10 lg:pt-24" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                <div>
                  <label htmlFor="contact-name" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-[#808080] mb-1">
                    01 — Name *
                  </label>
                  <input
                    id="contact-name"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane North"
                    className={inputCls}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-[#808080] mb-1">
                    02 — Email *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@company.com"
                    className={inputCls}
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                <div>
                  <label htmlFor="contact-phone" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-[#808080] mb-1">
                    03 — Phone
                  </label>
                  <input
                    id="contact-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+1 (___) ___-____"
                    className={inputCls}
                    data-testid="contact-phone-input"
                  />
                </div>
                <div className="relative">
                  <label htmlFor="contact-service" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-[#808080] mb-1">
                    04 — Service *
                  </label>
                  <select
                    id="contact-service"
                    required
                    value={form.service}
                    onChange={set("service")}
                    className={`${inputCls} appearance-none cursor-pointer`}
                    data-testid="contact-service-select"
                  >
                    <option value="" disabled className="bg-[#121212]">Select a service</option>
                    <option value="General Construction" className="bg-[#121212]">General Construction</option>
                    <option value="Home Renovation & Remodeling" className="bg-[#121212]">Home Renovation & Remodeling</option>
                    <option value="Concrete" className="bg-[#121212]">Concrete</option>
                    <option value="Snow Removal" className="bg-[#121212]">Snow Removal</option>
                    <option value="Other" className="bg-[#121212]">Other</option>
                  </select>
                  <ArrowUpRight className="absolute right-0 bottom-4 h-4 w-4 rotate-45 text-[#FF3B00] pointer-events-none" />
                </div>
              </div>

              <div>
                <label htmlFor="contact-message" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-[#808080] mb-1">
                  05 — The Job *
                </label>
                <textarea
                  id="contact-message"
                  required
                  minLength={10}
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Scope, timeline, site address — give us the picture."
                  className={`${inputCls} resize-none normal-case`}
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                className="group relative w-full overflow-hidden bg-[#FF3B00] py-6 font-mono text-sm uppercase tracking-[0.3em] text-[#050505] disabled:opacity-60"
                data-testid="contact-submit-button"
              >
                <span className="absolute inset-0 -translate-y-full bg-[#F2F2F2] transition-transform duration-300 ease-out group-hover:translate-y-0" />
                <span className="relative z-10 flex items-center justify-center gap-3 font-bold">
                  {sending ? "Transmitting..." : "Send the enquiry"}
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </button>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
